import { Component, Input, Output, EventEmitter, inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MenubarItem } from '../menubar/menubar.component';

// Reuse MenubarItem interface for consistency
export type MenuItem = MenubarItem;

/**
 * CfiMenuComponent - Material Design menu replacement for PrimeNG Menu
 *
 * Migration from PrimeNG:
 * - Replace `<p-menu [model]="items" #menu></p-menu>`
 * - With `<cfi-menu [items]="items" #menu></cfi-menu>`
 * - MenuItem interface is compatible with MenubarItem
 *
 * @example
 * ```typescript
 * items: MenuItem[] = [
 *   { label: 'New', icon: 'add', command: () => this.create() },
 *   { label: 'Open', icon: 'folder_open', routerLink: '/open' },
 *   { separator: true },
 *   { label: 'Delete', icon: 'delete', command: () => this.delete() }
 * ];
 * ```
 *
 * ```html
 * <button mat-button [matMenuTriggerFor]="menu.matMenu">Options</button>
 * <cfi-menu #menu [items]="items"></cfi-menu>
 * ```
 */
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'cfi-menu',
  templateUrl: './menu.component.html',
  styleUrls: ['./menu.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule
  ],
  standalone: true
})
export class CfiMenuComponent {
  @Input() items: MenuItem[] = [];
  @Input() styleClass?: string;
  @Input() popup: boolean = true; // PrimeNG compatibility
  @Output() itemClick = new EventEmitter<MenuItem>();

  @ViewChild(MatMenuTrigger) matMenuTrigger?: MatMenuTrigger;

  private readonly router = inject(Router);

  /**
   * Get the MatMenu for use with matMenuTriggerFor
   */
  get matMenu() {
    return this.matMenuTrigger?.menu;
  }

  /**
   * Show the menu (for programmatic opening)
   */
  show(): void {
    this.matMenuTrigger?.openMenu();
  }

  /**
   * Hide the menu (for programmatic closing)
   */
  hide(): void {
    this.matMenuTrigger?.closeMenu();
  }

  /**
   * Toggle the menu visibility
   */
  toggle(): void {
    if (this.matMenuTrigger?.menuOpen) {
      this.hide();
    } else {
      this.show();
    }
  }

  /**
   * Handle menu item click
   */
  onItemClick(item: MenuItem, event?: Event): void {
    if (item.disabled) {
      event?.preventDefault();
      return;
    }

    // Execute command if provided
    if (item.command) {
      item.command();
    }

    // Navigate if routerLink is provided
    if (item.routerLink) {
      const link = Array.isArray(item.routerLink) ? item.routerLink : [item.routerLink];
      this.router.navigate(link, { queryParams: item.queryParams });
    }

    // Emit click event
    this.itemClick.emit(item);

    event?.stopPropagation();
  }

  /**
   * Check if item should be displayed
   */
  isVisible(item: MenuItem): boolean {
    return item.visible !== false;
  }

  /**
   * Check if item has submenu
   */
  hasSubmenu(item: MenuItem): boolean {
    return !!item.items && item.items.length > 0;
  }

  /**
   * Track by function for ngFor
   */
  trackByLabel(index: number, item: MenuItem): string {
    return item.label + index;
  }
}
